import type { Player } from "./types";

export const STORAGE_KEY = "achtung.localSetup";
const ARENA_MIGRATED_KEY = "achtung.localSetup.arena2to1";

export interface LocalSetupData {
  players: Player[];
  maxScore: number;
  mapString: string;
  // Power-up settings (optional; absent = engine defaults). See shared/powerups.
  disabledPowerUps?: string[];
  powerUpSpawnChance?: number;
  wormSpeed?: number;
}

export function loadLocalSetup(): LocalSetupData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!Array.isArray(data?.players) || data.players.length < 2) return null;
    if (typeof data.maxScore !== "number" || typeof data.mapString !== "string") return null;
    // Once: a setup saved on the old default arena (Square 700, before the 2:1
    // arenas) moves to the new default, the same-area Medium Rectangle. A square
    // picked after this stays picked.
    if (!localStorage.getItem(ARENA_MIGRATED_KEY)) {
      if (data.mapString === "Square 700") data.mapString = "Rectangle 990 495";
      localStorage.setItem(ARENA_MIGRATED_KEY, "1");
    }
    return data as LocalSetupData;
  } catch {
    return null;
  }
}

export function saveLocalSetup(data: LocalSetupData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore write errors (private browsing, quota exceeded, etc.)
  }
}

// Patches the left/right key bindings for players that are already in the saved
// setup (matched by id).  Players not in the saved setup are ignored — this
// prevents an online match (which may have a different roster with bot IDs) from
// overwriting the local player list.
export function updateLocalSetupPlayers(players: Player[]): void {
  const current = loadLocalSetup();
  if (!current) return;
  const updatedPlayers = current.players.map((saved) => {
    const live = players.find((p) => p.id === saved.id);
    return live ? { ...saved, left: live.left, right: live.right } : saved;
  });
  saveLocalSetup({ ...current, players: updatedPlayers });
}
